/**
 * Minimal "Add Company" modal used inline from forms (e.g. Contact create/edit)
 * when no company exists yet or the user wants to add one without leaving the page.
 *
 * Captures the four fields the user requested (name, email, phone, website).
 * On success, calls `onCreated(company)` so the parent can auto-select the new id.
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/forms/FormField';
import { useCompanies } from '@/composables/crm/useCompanies';
import type { Company } from '@/types/table';

const schema = z.object({
  name: z.string().min(1, 'Company name is required').max(200),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().max(50).optional().or(z.literal('')),
  website: z.string().max(200).optional().or(z.literal('')),
});
type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (company: Company) => void;
}

export function CompanyQuickCreateDialog({ open, onOpenChange, onCreated }: Props) {
  const { createCompany } = useCompanies();
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', phone: '', website: '' },
  });

  useEffect(() => {
    if (open) form.reset({ name: '', email: '', phone: '', website: '' });
  }, [open, form]);

  const onSubmit = (data: FormData) => {
    createCompany.mutate(data, {
      onSuccess: (res) => {
        if (res?.data) {
          onCreated(res.data);
          onOpenChange(false);
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Company</DialogTitle>
          <DialogDescription>Fill in the basics — you can add address, industry, etc. later from the Companies page.</DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField form={form} name="name" label="Company Name" required />
          <FormField form={form} name="email" label="Email" type="email" />
          <FormField form={form} name="phone" label="Phone" />
          <FormField form={form} name="website" label="Website" placeholder="https://example.com" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={createCompany.isPending}>{createCompany.isPending ? 'Creating…' : 'Add Company'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
